// CB:BEGIN app-user-context
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

type AppUserRole = 'CONTRACTOR' | 'HOMEOWNER' | 'ADMIN' | 'UNKNOWN' | null;

// Keep in sync with useContractorReadiness VerificationState
type VerificationState = 'approved' | 'pending' | 'not_submitted';

interface AppUserContextValue {
  loading: boolean;
  error: string | null;
  role: AppUserRole;
  clerkId: string | null;
  contractorsSelf: any | null;
  contractorsLoading: boolean;
  contractorsError: string | null;
  refresh: () => Promise<void>;
}

const AppUserContext = createContext<AppUserContextValue | undefined>(undefined);

export const AppUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<AppUserRole>(null);
  const [contractorsSelf, setContractorsSelf] = useState<any | null>(null);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [contractorsError, setContractorsError] = useState<string | null>(null);

  const clerkId = user?.id ?? null;

  const load = useCallback(async () => {
    // Wait for Clerk to finish initializing
    if (!isLoaded) {
      setLoading(true);
      return;
    }

    if (!isSignedIn || !user) {
      setLoading(false);
      setError(null);
      setRole(null);
      setContractorsSelf(null);
      setContractorsLoading(false);
      setContractorsError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setContractorsSelf(null);
    setContractorsLoading(false);
    setContractorsError(null);

    let resolvedRole: AppUserRole = 'UNKNOWN';

    try {
      const token = await getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch /api/me for canonical role
      const meRes = await fetch(getApiUrl('/api/me'), {
        credentials: 'include',
        headers,
      });

      if (meRes.ok) {
        const meJson = await meRes.json();
        const rawRole = String(meJson?.role || '').toUpperCase();
        if (rawRole === 'CONTRACTOR' || rawRole === 'HOMEOWNER' || rawRole === 'ADMIN') {
          resolvedRole = rawRole as AppUserRole;
        } else {
          resolvedRole = 'UNKNOWN';
        }
      } else {
        // If /api/me fails (e.g. compliance not accepted yet), surface UNKNOWN but don't crash
        resolvedRole = 'UNKNOWN';
      }
    } catch (e: any) {
      console.error('[AppUserContext] Failed to load /api/me', e);
      setError(e?.message || 'Failed to load user session');
      resolvedRole = 'UNKNOWN';
    }

    setRole(resolvedRole);

    // If contractor, also eagerly fetch unified readiness from /api/contractors/self
    if (resolvedRole === 'CONTRACTOR' && clerkId) {
      setContractorsLoading(true);
      setContractorsError(null);
      try {
        const token = await getToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const selfUrl = getApiUrl(`/api/contractors/self?userId=${encodeURIComponent(clerkId)}`);
        const selfRes = await fetch(selfUrl, {
          credentials: 'include',
          headers,
        });
        if (!selfRes.ok) {
          throw new Error(
            selfRes.status === 401
              ? 'Please sign in again.'
              : selfRes.status === 404
              ? 'Endpoint not found. Ensure backend is up to date.'
              : `Service error: ${selfRes.status}`
          );
        }
        const selfText = await selfRes.text();
        if (selfText.trim().startsWith('<!') || selfText.trim().startsWith('<html')) {
          throw new Error('API returned HTML instead of JSON for /api/contractors/self');
        }
        const selfJson = JSON.parse(selfText);

        // Basic sanity check so we don't accidentally store junk
        const verificationStatus: string | undefined = selfJson?.verification?.status;
        const vState: VerificationState =
          verificationStatus === 'APPROVED' || selfJson?.verification?.isVerified
            ? 'approved'
            : verificationStatus === 'SUBMITTED'
            ? 'pending'
            : 'not_submitted';

        setContractorsSelf({
          ...selfJson,
          verificationState: vState,
        });
      } catch (e: any) {
        console.error('[AppUserContext] Failed to load /api/contractors/self', e);
        setContractorsError(e?.message || 'Failed to load contractor readiness');
      } finally {
        setContractorsLoading(false);
      }
    }

    setLoading(false);
  }, [isLoaded, isSignedIn, user, getToken, clerkId]);

  useEffect(() => {
    // Fire once when auth state changes
    load();
  }, [load]);

  const value = useMemo<AppUserContextValue>(
    () => ({
      loading,
      error,
      role,
      clerkId,
      contractorsSelf,
      contractorsLoading,
      contractorsError,
      refresh: load,
    }),
    [loading, error, role, clerkId, contractorsSelf, contractorsLoading, contractorsError, load]
  );

  return <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>;
};

export function useAppUser(): AppUserContextValue {
  const ctx = useContext(AppUserContext);
  if (!ctx) {
    throw new Error('useAppUser must be used within an AppUserProvider');
  }
  return ctx;
}
// CB:END app-user-context

